import { session } from '../../../webrtc/session';
import { CallParticipant } from '../../useCallParticipants';
import type { Subscriber, SubscriberBrokenReason } from './types';

export const getSubscriberBrokenReason = ():
  | SubscriberBrokenReason
  | undefined => {
  if (typeof window === 'undefined' || !window.OT) {
    return;
  }

  /**
   * We are going to check only 1-1 call.
   */
  const participantsInTheCall = Object.values(
    session.participantsMap,
  ) as CallParticipant[];

  const participant = participantsInTheCall[0];

  if (participantsInTheCall.length !== 1 || participant === undefined) {
    return;
  }

  /**
   * Find the subscriber for the participant video.
   */
  const subscribers = (
    window as unknown as { OT: { subscribers: Subscriber[] } }
  ).OT.subscribers
    .map((s: Subscriber) => s)
    .filter((s) => {
      if (!s.stream?.connection) {
        return false;
      }

      try {
        const connectionData = JSON.parse(s.stream.connection.data);
        return connectionData.identityId === participant.id;
      } catch {
        // If JSON parsing fails, treat this subscriber as invalid
        return false;
      }
    }) as Subscriber[];

  /**
   * If we do not have subscribers it means that we have not yet subscribed or subscriber destroyed and new one not created.
   */
  if (subscribers.length === 0) {
    return 'psStatusSubSubscriberMissing';
  }

  /**
   * Now we need to find a subscribers with the video source with type `camera`
   * and make sure we do not have more then one.
   */
  const subscribersWithCameraSource = subscribers.filter(
    (s: Subscriber) => s.stream?.videoType === 'camera',
  );

  /**
   * If there are no subscribers with camera source - it is a problem. Video will be disabled.
   * Maybe media access got revoked on publisher side.
   */
  if (subscribersWithCameraSource.length === 0) {
    return 'psStatusSubCameraSourceMissing';
  }

  if (subscribersWithCameraSource.length > 1) {
    return 'psStatusSubMultipleSubscribes';
  }

  const subscriber = subscribersWithCameraSource[0] as Subscriber;

  /**
   * Video has been disabled by the user or possibly something else.
   */
  if (!subscriber.stream?.hasVideo) {
    return 'psStatusSubVideoDisabled';
  }

  /**
   * Checking that video element is properly rendering.
   */
  const videoElement = subscriber.videoElement();

  if (!videoElement || videoElement.readyState < 2) {
    return 'psStatusSubVideoNotRendering';
  }

  const videoTrack = (
    videoElement.srcObject as MediaStream
  )?.getVideoTracks()[0];

  if (!videoTrack) {
    return 'psStatusSubVideoHasNoTrack';
  }

  if (videoTrack.muted) {
    return 'psStatusSubVideoTrackMuted';
  }

  if (!videoTrack.enabled) {
    return 'psStatusSubVideoTrackDisabled';
  }

  return;
};
